/** Browser-reported IANA time zone (e.g. Europe/London). */
export function getBrowserIanaTimeZoneId(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Today's calendar date as yyyy-MM-dd in the given IANA zone, or UTC date if missing/invalid. */
export function getCalendarTodayIsoInTimeZone(ianaTimeZoneId: string | null | undefined): string {
  const now = new Date();
  const tz = ianaTimeZoneId?.trim();
  if (!tz) {
    return now.toISOString().slice(0, 10);
  }
  try {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const parts = fmt.formatToParts(now);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    if (y && m && d) {
      return `${y}-${m}-${d}`;
    }
  } catch {
    /* use UTC */
  }
  return now.toISOString().slice(0, 10);
}

function parseYmd(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
  return { y, m, d };
}

function formatYmd(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Calendar month arithmetic aligned with .NET DateOnly.AddMonths-style clamping. */
function subtractCalendarMonths(y: number, month: number, day: number, months: number): { y: number; m: number; d: number } {
  let m0 = month - 1 - months;
  const yAdj = y + Math.floor(m0 / 12);
  m0 = ((m0 % 12) + 12) % 12;
  const dim = new Date(Date.UTC(yAdj, m0 + 1, 0)).getUTCDate();
  const d = Math.min(day, dim);
  return { y: yAdj, m: m0 + 1, d };
}

function subtractCalendarYears(y: number, month: number, day: number, years: number): { y: number; m: number; d: number } {
  return subtractCalendarMonths(y, month, day, years * 12);
}

/** Rolling "N years/months before today's date" in the enquiry calendar (yyyy-MM-dd). */
export function subtractRelativeFromCalendarToday(
  amount: number,
  unit: "years" | "months",
  ianaTimeZoneId: string | null | undefined
): string {
  const todayIso = getCalendarTodayIsoInTimeZone(ianaTimeZoneId);
  const { y, m, d } = parseYmd(todayIso);
  const r = unit === "years" ? subtractCalendarYears(y, m, d, amount) : subtractCalendarMonths(y, m, d, amount);
  return formatYmd(r.y, r.m, r.d);
}
